import { CloudUploadOutlined } from "@ant-design/icons";
import { AutoComplete, Button, Modal } from "antd";
import Dragger from "antd/es/upload/Dragger";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store/store";
import { UploadDocument } from "../../../../models/EnhancementSlice";
import { errorMessage } from "../../../../utils/notificationUtils";
import FilePreviewModal from "../../PreAuth-query/FilePreviewModal";

export type DocumentMasterOption = {
  id: number;
  label: string;
  labelId: number;
  documentUniqueCode?: string;
};

type UploadRequestExtraDocumentAyushmanProps = {
  visible: boolean;
  onCancel: () => void;
  documentMasterOptions: DocumentMasterOption[];
  disabled?: boolean;
  onUploadAttached: (
    uploadResponse: any[],
    meta: {
      selectedMasterId: string | null;
      documentMasterName: string | null;
    }
  ) => Promise<void>;
};

const UploadRequestExtraDocumentAyushman: FC<
  UploadRequestExtraDocumentAyushmanProps
> = ({
  visible,
  onCancel,
  documentMasterOptions,
  disabled,
  onUploadAttached,
}) => {
  const { patients, auth, enhancement } = useSelector(
    (state: RootState) => state
  );
  const dispatch = useDispatch<AppDispatch>();

  const [documentNameInput, setDocumentNameInput] = useState("");
  const [selectedMasterId, setSelectedMasterId] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewName, setPreviewName] = useState("");

  const autocompleteOptions = useMemo(
    () =>
      documentMasterOptions.map((item) => ({
        value: item.label,
        label: item.label,
        masterId: String(item.labelId),
      })),
    [documentMasterOptions]
  );

  useEffect(() => {
    if (!visible) {
      setDocumentNameInput("");
      setSelectedMasterId(null);
      setFile(null);
      setPreviewOpen(false);
      setPreviewUrl("");
      setPreviewName("");
    }
  }, [visible]);

  const resolveMasterFromInput = (input: string): string | null => {
    const trimmed = input.trim();
    if (!trimmed) return null;
    const exact = documentMasterOptions.find(
      (item) => item.label.toLowerCase() === trimmed.toLowerCase()
    );
    return exact ? String(exact.labelId) : null;
  };

  const handleDocumentNameChange = (value: string) => {
    setDocumentNameInput(value);
    const match = documentMasterOptions.find(
      (item) => item.label.toLowerCase() === value.trim().toLowerCase()
    );
    setSelectedMasterId(match ? String(match.labelId) : null);
  };

  const handleDocumentNameSelect = (value: string, option: any) => {
    setDocumentNameInput(value);
    setSelectedMasterId(option?.masterId ?? resolveMasterFromInput(value));
  };

  const handleFileChange = (uploadFile: File) => {
    const trimmed = documentNameInput.trim();
    if (!trimmed) {
      errorMessage("Please enter or select a document type");
      return;
    }
    setFile(uploadFile);
    setPreviewUrl(URL.createObjectURL(uploadFile));
    setPreviewName(uploadFile.name);
    setPreviewOpen(true);
  };

  const handleUpload = async () => {
    if (!file) {
      errorMessage("Please select a file to upload");
      return;
    }

    const trimmedName = documentNameInput.trim();
    if (!trimmedName) {
      errorMessage("Please enter or select a document type");
      return;
    }

    const masterId =
      selectedMasterId ?? resolveMasterFromInput(trimmedName);
    const isCustomMaster = !masterId;

    const CollectPostData = new FormData();
    const healthCareId =
      patients?.fetchBasicDetails?.casesResponse?.healthCareId;
    const patientId = patients?.fetchBasicDetails?.patientResponse?.id;
    const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
    const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;

    if (healthCareId) {
      CollectPostData.append("hospitalId", healthCareId.toString());
    }
    if (patientId) {
      CollectPostData.append("patientId", patientId.toString());
    }
    if (caseId) {
      CollectPostData.append("caseId", caseId.toString());
    }
    if (visitId) {
      CollectPostData.append("visitId", visitId.toString());
    }

    const documentMasterName = isCustomMaster
      ? trimmedName.toLowerCase()
      : null;

    if (isCustomMaster && documentMasterName) {
      CollectPostData.append("documentMasterName", documentMasterName);
    } else if (masterId) {
      CollectPostData.append("documentType", masterId);
    }

    CollectPostData.append("file", file);

    try {
      const resp = await dispatch(
        UploadDocument({
          formData: CollectPostData,
          accessToken: auth.accessToken || "",
        })
      ).unwrap();

      if (!resp?.length) {
        errorMessage("Please upload again");
        return;
      }

      await onUploadAttached(resp, {
        selectedMasterId: masterId,
        documentMasterName,
      });

      setPreviewOpen(false);
      onCancel();
    } catch (error) {
      console.error("Failed to upload document:", error);
      errorMessage("Failed to upload document. Please try again.");
    }
  };

  return (
    <>
      <Modal
        open={visible}
        onCancel={onCancel}
        centered
        width={700}
        footer={null}
        title="Upload additional request document"
        destroyOnClose
      >
        <div
          style={{
            border: "0.1px solid rgb(217, 217, 217)",
            borderRadius: "10px",
            padding: "17px",
            backgroundColor: "#FFFFFF",
          }}
        >
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontWeight: 700, marginBottom: 8 }}>
              Document type
            </label>
            <AutoComplete
              style={{ width: "100%" }}
              // options={autocompleteOptions}
              options={[]}
              value={documentNameInput}
              onChange={handleDocumentNameChange}
              onSelect={handleDocumentNameSelect}
              placeholder="Search document master or type a new name"
              disabled={disabled}
              filterOption={(inputValue, option) =>
                (option?.value as string)
                  ?.toLowerCase()
                  .includes(inputValue.toLowerCase()) ?? false
              }
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "1rem",
                fontWeight: 700,
              }}
            >
              Select file
            </label>
            <Dragger
              name="file"
              multiple={false}
              disabled={disabled || !documentNameInput.trim()}
              beforeUpload={() => false}
              showUploadList={false}
              onChange={(info) => {
                const uploadFile = info.file;
                if (uploadFile) {
                  handleFileChange(uploadFile as unknown as File);
                }
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                }}
              >
                <CloudUploadOutlined
                  style={{
                    color: "#008B8B",
                    fontSize: "30px",
                    marginRight: "20px",
                  }}
                />
                <div>
                  <span style={{ fontWeight: 500, fontSize: "14px" }}>
                    Drag & drop or browse file
                  </span>
                  <p
                    style={{
                      color: "#8c8c8c",
                      fontSize: "13px",
                      margin: 0,
                    }}
                  >
                    (jpeg, png, gif, bmp, wemp, pdf)
                  </p>
                </div>
              </div>
            </Dragger>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
          <Button type="primary" onClick={onCancel} style={{ minWidth: 100 }}>
            Cancel
          </Button>
        </div>
      </Modal>

      <FilePreviewModal
        isVisible={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fileUrl={previewUrl}
        fileName={previewName}
        handleUpload={handleUpload}
        showUploadButton
        key={file?.name || "no-file"}
        isLoading={enhancement?.isLoading}
      />
    </>
  );
};

export default UploadRequestExtraDocumentAyushman;
